<template>
  <div class="key-terms-card" :class="{ compact }">
    <div class="key-terms-header">
      <i class="el-icon-collection-tag"></i>
      <span>Key Terms</span>
    </div>

    <div v-if="terms.length > 0" class="key-terms-list">
      <div v-for="(item, index) in terms" :key="index" class="key-term-item">
        <div class="key-term-name">{{ item.term }}</div>
        <div v-if="item.definition" class="key-term-definition">{{ item.definition }}</div>
      </div>
    </div>

    <div v-else class="key-terms-empty">No key terms available.</div>
  </div>
</template>

<script>
export default {
  name: 'KeyTermsCard',
  props: {
    card: {
      type: Object,
      required: true
    },
    compact: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    terms() {
      return this.normalizeTerms(this.getTermSource(this.card))
    }
  },
  methods: {
    getTermSource(card) {
      return (
        card.key_terms ||
        card.keyTerms ||
        card.terms ||
        card.term_list ||
        card.items ||
        card.content ||
        []
      )
    },
    normalizeTerms(source) {
      const value = this.parseSource(source)

      if (Array.isArray(value)) {
        return value.map(this.normalizeTermItem).filter(item => item.term)
      }

      if (value && typeof value === 'object') {
        const nested = value.key_terms || value.keyTerms || value.terms || value.items
        if (Array.isArray(nested)) {
          return nested.map(this.normalizeTermItem).filter(item => item.term)
        }
        return [this.normalizeTermItem(value)].filter(item => item.term)
      }

      if (typeof value === 'string') {
        return this.parseTextTerms(value)
      }

      return []
    },
    parseSource(source) {
      if (typeof source !== 'string') return source
      const trimmed = source.trim()
      if (!trimmed) return []

      if (
        (trimmed.startsWith('[') && trimmed.endsWith(']')) ||
        (trimmed.startsWith('{') && trimmed.endsWith('}'))
      ) {
        try {
          return JSON.parse(trimmed)
        } catch (error) {
          return trimmed
        }
      }

      return trimmed
    },
    normalizeTermItem(item) {
      if (typeof item === 'string') {
        return this.parseTermLine(item)
      }

      if (!item || typeof item !== 'object') {
        return { term: '', definition: '' }
      }

      return {
        term: (
          item.term ||
          item.key_term ||
          item.keyTerm ||
          item.name ||
          item.title ||
          item.keyword ||
          ''
        ).toString().trim(),
        definition: (
          item.definition ||
          item.meaning ||
          item.description ||
          item.explanation ||
          item.content ||
          ''
        ).toString().trim()
      }
    },
    parseTextTerms(text) {
      return text
        .split(/\n|;/)
        .map(line => line.replace(/^\s*[-*\d.)]+\s*/, '').trim())
        .filter(Boolean)
        .map(this.parseTermLine)
        .filter(item => item.term)
    },
    parseTermLine(line) {
      const separatorMatch = line.match(/^(.+?)(?:\s+-\s+|:\s+)(.+)$/)
      if (!separatorMatch) {
        return { term: line.trim(), definition: '' }
      }

      return {
        term: separatorMatch[1].trim(),
        definition: separatorMatch[2].trim()
      }
    }
  }
}
</script>

<style scoped>
.key-terms-card {
  background: #f7f9fc;
  border: 1px solid #dfe8f5;
  border-radius: 8px;
  padding: 18px;
  color: #303133;
}

.key-terms-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
  color: #2f6fbd;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.key-terms-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.key-term-item {
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  padding: 12px 14px;
}

.key-term-name {
  color: #1f2d3d;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.35;
}

.key-term-definition {
  margin-top: 6px;
  color: #606266;
  font-size: 14px;
  line-height: 1.55;
}

.key-terms-empty {
  color: #909399;
  font-size: 14px;
  font-style: italic;
}

.key-terms-card.compact {
  padding: 14px;
}

.key-terms-card.compact .key-terms-header {
  font-size: 12px;
  margin-bottom: 10px;
}

.key-terms-card.compact .key-term-item {
  padding: 10px 12px;
}

.key-terms-card.compact .key-term-name {
  font-size: 14px;
}

.key-terms-card.compact .key-term-definition {
  font-size: 13px;
}
</style>
